import React from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";
import AutoFixHighOutlined from "@mui/icons-material/AutoFixHighOutlined";
import DownloadOutlined from "@mui/icons-material/DownloadOutlined";
import GraphicEqOutlined from "@mui/icons-material/GraphicEqOutlined";
import PauseOutlined from "@mui/icons-material/PauseOutlined";
import PlayArrowOutlined from "@mui/icons-material/PlayArrowOutlined";
import { GlobalContext } from "../../context/GlobalContext";
import PageHeader from "../../common/PageHeader";

const ACCEPT_AUDIO = "audio/mpeg,audio/mp3,audio/wav,audio/x-wav,audio/webm,audio/mp4,audio/x-m4a,.mp3,.wav,.m4a,.webm";
const MAX_BYTES = 25 * 1024 * 1024;

function voiceSubtitle(v) {
  const parts = [];
  if (v.category) parts.push(v.category);
  const labels = v.labels || {};
  if (labels.accent) parts.push(labels.accent);
  if (labels.gender) parts.push(labels.gender);
  if (labels.age) parts.push(labels.age);
  return parts.join(" · ");
}

export default function VoiceChanger({ lang }) {
  const theme = useTheme();
  const { hitAxios, showSnack } = React.useContext(GlobalContext);

  const [config, setConfig] = React.useState({ configured: false, models: [] });
  const [voices, setVoices] = React.useState([]);
  const [loadingVoices, setLoadingVoices] = React.useState(true);
  const [voiceId, setVoiceId] = React.useState("");
  const [modelId, setModelId] = React.useState("eleven_multilingual_sts_v2");
  const [removeNoise, setRemoveNoise] = React.useState(false);
  const [title, setTitle] = React.useState("");
  const [audioFile, setAudioFile] = React.useState(null);
  const [sourcePreviewUrl, setSourcePreviewUrl] = React.useState(null);
  const [outputUrl, setOutputUrl] = React.useState(null);
  const [submitting, setSubmitting] = React.useState(false);
  const [error, setError] = React.useState("");
  const [playingId, setPlayingId] = React.useState(null);
  const previewRef = React.useRef(null);

  const notify = React.useCallback(
    (message, success = false) => {
      if (message) showSnack?.(message, success);
    },
    [showSnack],
  );

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      const res = await hitAxios({
        path: "/api/voice-changer/config",
        post: false,
        showLoading: false,
        showSnackbar: false,
      });
      if (!cancelled && res?.data?.success) {
        setConfig(res.data);
        if (res.data.models?.[0]?.id) setModelId(res.data.models[0].id);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [hitAxios]);

  React.useEffect(() => {
    let cancelled = false;
    setLoadingVoices(true);
    (async () => {
      const res = await hitAxios({
        path: "/api/voice-changer/voices",
        post: false,
        showLoading: false,
        showSnackbar: false,
      });
      if (cancelled) return;
      setLoadingVoices(false);
      if (res?.data?.success) {
        const list = res.data.data?.voices || [];
        setVoices(list);
        if (list.length && !voiceId) setVoiceId(list[0].voice_id);
      } else if (res?.data?.msg) {
        setError(res.data.msg);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- load once
  }, [hitAxios]);

  React.useEffect(() => {
    return () => {
      if (sourcePreviewUrl) URL.revokeObjectURL(sourcePreviewUrl);
    };
  }, [sourcePreviewUrl]);

  const stopPreview = () => {
    if (previewRef.current) {
      previewRef.current.pause();
      previewRef.current = null;
    }
    setPlayingId(null);
  };

  const playPreview = (url, id) => {
    if (!url) return;
    stopPreview();
    const audio = new Audio(url);
    previewRef.current = audio;
    setPlayingId(id);
    audio.play().catch(() => setPlayingId(null));
    audio.onended = () => setPlayingId(null);
  };

  const onPickFile = (file) => {
    if (!file) return;
    if (file.size > MAX_BYTES) {
      setError(lang?.voiceChangerFileTooLarge || "Audio must be 25 MB or smaller.");
      return;
    }
    setError("");
    setOutputUrl(null);
    setAudioFile(file);
    setSourcePreviewUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return URL.createObjectURL(file);
    });
    if (!title.trim()) {
      const base = file.name.replace(/\.[^.]+$/, "");
      setTitle(base.slice(0, 80));
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    const file = e.dataTransfer?.files?.[0];
    if (file) onPickFile(file);
  };

  const canSubmit = config.configured && audioFile && voiceId && !submitting;

  const handleConvert = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    setError("");
    setOutputUrl(null);
    try {
      const fd = new FormData();
      fd.append("audio", audioFile);
      fd.append("voice_id", voiceId);
      fd.append("model_id", modelId);
      fd.append("remove_background_noise", removeNoise ? "true" : "false");
      if (title.trim()) fd.append("title", title.trim());

      const res = await hitAxios({
        path: "/api/voice-changer/convert",
        post: true,
        obj: fd,
        showLoading: false,
        showSnackbar: false,
        timeout: 300000,
      });

      if (!res?.data?.success) {
        throw new Error(res?.data?.msg || lang?.voiceChangerFailed || "Voice conversion failed.");
      }

      const url = res.data.data?.output_url;
      if (!url) throw new Error("Server did not return an output URL.");
      setOutputUrl(url);
      notify(lang?.voiceChangerDone || "Voice conversion complete.", true);
    } catch (err) {
      const msg = err?.message || lang?.voiceChangerFailed || "Voice conversion failed.";
      setError(msg);
      notify(msg, false);
    } finally {
      setSubmitting(false);
    }
  };

  const selectedVoice = voices.find((v) => v.voice_id === voiceId);

  return (
    <Box sx={{ maxWidth: 1100, mx: "auto", px: { xs: 2, sm: 3 }, py: 3 }}>
      <PageHeader
        title={lang?.voiceChangerTitle || "Voice Changer"}
        subtitle={
          lang?.voiceChangerSubtitle ||
          "Upload audio and transform it into any ElevenLabs voice while keeping timing and emotion."
        }
        icon={GraphicEqOutlined}
      />

      {!config.configured && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {lang?.voiceChangerNotConfigured ||
            "ElevenLabs is not configured on the server. Add ELEVENLABS_API_KEY to your .env file and restart the backend."}
        </Alert>
      )}

      <Grid container spacing={2.5}>
        <Grid item xs={12} md={7}>
          <Card variant="outlined" sx={{ borderRadius: 2 }}>
            <CardContent>
              <Stack spacing={2.5}>
                <TextField
                  fullWidth
                  size="small"
                  label={lang?.voiceChangerLabelTitle || "Label"}
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder={lang?.voiceChangerLabelPlaceholder || "e.g. Podcast intro take 2"}
                />

                <Box
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={onDrop}
                  sx={{
                    border: "1px dashed",
                    borderColor: alpha(theme.palette.text.primary, 0.25),
                    borderRadius: 2,
                    p: 3,
                    textAlign: "center",
                    bgcolor: alpha(theme.palette.primary.main, 0.03),
                    cursor: "pointer",
                  }}
                  onClick={() => document.getElementById("vc-audio-input")?.click()}
                >
                  <input
                    id="vc-audio-input"
                    type="file"
                    accept={ACCEPT_AUDIO}
                    hidden
                    onChange={(e) => onPickFile(e.target.files?.[0])}
                  />
                  <GraphicEqOutlined sx={{ fontSize: 36, color: "primary.main", mb: 1 }} />
                  <Typography variant="subtitle2" fontWeight={700}>
                    {lang?.voiceChangerUpload || "Source audio"}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block" mt={0.5}>
                    {lang?.voiceChangerUploadHint || "MP3, WAV, M4A, or WebM — max 25 MB"}
                  </Typography>
                  {audioFile && (
                    <Typography variant="body2" sx={{ mt: 1.5, fontWeight: 600 }}>
                      {audioFile.name}
                    </Typography>
                  )}
                </Box>

                {sourcePreviewUrl && (
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Typography variant="caption" color="text.secondary" fontWeight={600}>
                      {lang?.voiceChangerSourcePreview || "Source preview"}
                    </Typography>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={
                        playingId === "source" ? <PauseOutlined /> : <PlayArrowOutlined />
                      }
                      onClick={() =>
                        playingId === "source"
                          ? stopPreview()
                          : playPreview(sourcePreviewUrl, "source")
                      }
                    >
                      {playingId === "source"
                        ? lang?.voiceChangerPause || "Pause"
                        : lang?.voiceChangerPlay || "Play"}
                    </Button>
                  </Stack>
                )}

                <FormControl fullWidth size="small" disabled={loadingVoices || !voices.length}>
                  <InputLabel id="vc-voice-label">
                    {lang?.voiceChangerTargetVoice || "Target voice"}
                  </InputLabel>
                  <Select
                    labelId="vc-voice-label"
                    label={lang?.voiceChangerTargetVoice || "Target voice"}
                    value={voiceId}
                    onChange={(e) => setVoiceId(e.target.value)}
                  >
                    {voices.map((v) => (
                      <MenuItem key={v.voice_id} value={v.voice_id}>
                        <Stack>
                          <Typography variant="body2" fontWeight={600}>
                            {v.name}
                          </Typography>
                          {voiceSubtitle(v) && (
                            <Typography variant="caption" color="text.secondary">
                              {voiceSubtitle(v)}
                            </Typography>
                          )}
                        </Stack>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {selectedVoice?.preview_url && (
                  <Button
                    size="small"
                    variant="text"
                    startIcon={
                      playingId === selectedVoice.voice_id ? (
                        <PauseOutlined />
                      ) : (
                        <PlayArrowOutlined />
                      )
                    }
                    onClick={() =>
                      playingId === selectedVoice.voice_id
                        ? stopPreview()
                        : playPreview(selectedVoice.preview_url, selectedVoice.voice_id)
                    }
                    sx={{ alignSelf: "flex-start" }}
                  >
                    {lang?.voiceChangerPreviewVoice || "Preview target voice"}
                  </Button>
                )}

                <FormControl fullWidth size="small">
                  <InputLabel id="vc-model-label">
                    {lang?.voiceChangerModel || "Model"}
                  </InputLabel>
                  <Select
                    labelId="vc-model-label"
                    label={lang?.voiceChangerModel || "Model"}
                    value={modelId}
                    onChange={(e) => setModelId(e.target.value)}
                  >
                    {(config.models || []).map((m) => (
                      <MenuItem key={m.id} value={m.id}>
                        {m.label || m.id}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControlLabel
                  control={
                    <Switch
                      checked={removeNoise}
                      onChange={(e) => setRemoveNoise(e.target.checked)}
                    />
                  }
                  label={
                    lang?.voiceChangerRemoveNoise ||
                    "Remove background noise from source audio"
                  }
                />

                {error && <Alert severity="error">{error}</Alert>}

                <Button
                  variant="contained"
                  size="large"
                  startIcon={
                    submitting ? <CircularProgress size={18} color="inherit" /> : <AutoFixHighOutlined />
                  }
                  disabled={!canSubmit}
                  onClick={handleConvert}
                  sx={{ fontWeight: 800 }}
                >
                  {submitting
                    ? lang?.voiceChangerConverting || "Converting…"
                    : lang?.voiceChangerConvert || "Convert voice"}
                </Button>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={5}>
          <Card
            variant="outlined"
            sx={{
              borderRadius: 2,
              height: "100%",
              bgcolor: alpha(theme.palette.success.main, 0.04),
              borderColor: alpha(theme.palette.success.main, 0.25),
            }}
          >
            <CardContent>
              <Typography variant="subtitle1" fontWeight={800} gutterBottom>
                {lang?.voiceChangerOutput || "Converted audio"}
              </Typography>
              {!outputUrl ? (
                <Typography variant="body2" color="text.secondary" lineHeight={1.6}>
                  {lang?.voiceChangerOutputHint ||
                    "Your transformed clip will appear here after conversion. Download it or use it in Video Studio."}
                </Typography>
              ) : (
                <Stack spacing={2}>
                  <audio controls src={outputUrl} style={{ width: "100%" }} />
                  <Button
                    variant="outlined"
                    startIcon={<DownloadOutlined />}
                    component="a"
                    href={outputUrl}
                    download={`${(title || "voice-changed").replace(/\s+/g, "-")}.mp3`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {lang?.voiceChangerDownload || "Download MP3"}
                  </Button>
                </Stack>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}
